import { useState, useEffect } from "react";

/**
 * Fetch data from API, replace parameters in API endpoint config
 * Uses `replacePlaceholders` to replace `params` in `urlTemplate`
 * @param {String} urlTemplate
 * @param {Object} params
 * @returns {Object}
 */
async function fetchDataFromUrl(urlTemplate, params = {}) {
  const url = replacePlaceholders(urlTemplate, params);

  const response = await fetch(url);
  const data = await response.json();

  return data;
}

/**
 * Replace placeholders in string
 * @param {String} template Placeholders are specified as {key}
 * @param {Object} params Each key is searched in `urlTemplate` and replaced with its value
 * @returns {String}
 */
function replacePlaceholders(template = "", params = {}) {
  let str = template;

  Object.entries(params).forEach(([key, value]) => {
    str = str.replace(new RegExp(`{${key}}`, "g"), value);
  });

  return str;
}

/**
 * Format number with thousand separators
 * @param {Number} number
 * @param {String} [prefix] Optional prefix to add
 * @returns {String}
 */
function formatNumber(number = 0, prefix = "CHF ") {
  const formatted = number.toLocaleString("de-CH");
  const prefixed = `${prefix}${formatted}`;

  return prefixed;
}

/**
 * Highlight numbers
 * @param {String|Number} str
 * @param {String} [prefix="CHF "] Optional prefix to look for
 * @returns {String}
 */
function highlightNumbers(input = "", prefix = "CHF ") {
  const str = `${input}`;
  const regexp = new RegExp(`${prefix}(\\d+)`, "g");
  const highlighted = str.replace(regexp, (match, p1) => {
    const value = parseInt(p1, 10);
    const formatted = formatNumber(value, prefix);

    return `<span class="Number">${formatted}</span>`;
  });

  return highlighted;
}

/**
 * Count from 0 to number
 * @param {Number} number
 * @param {Object} [options]
 * @param {Number} [options.duration=2000] How long to count for [ms]
 * @param {Number} [options.interval=10] How long to wait between steps [ms]
 * @param {Boolean} [options.isEnabled=true] Whether to count. Useful when combined with IntersectionObserver, e.g.
 * @returns {Number}
 */
function useCountNumber(
  number,
  { duration = 2000, interval = 10, isEnabled = true } = {}
) {
  const [currentNumber, setCurrentNumber] = useState(0);

  useEffect(() => {
    if (currentNumber === number || !isEnabled) {
      return;
    }

    const step = Math.max(1, parseInt((number / duration) * interval, 10));
    let counterNumber = 0;

    const counter = setInterval(() => {
      if (counterNumber >= number) {
        counterNumber = number;

        setCurrentNumber(counterNumber);

        return clearInterval(counter);
      }

      counterNumber = counterNumber + step;

      setCurrentNumber(counterNumber);
    }, interval);

    return () => clearInterval(counter);
  }, [number, duration, interval, isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return currentNumber;
}

export {
  fetchDataFromUrl,
  replacePlaceholders,
  formatNumber,
  highlightNumbers,
  useCountNumber,
};
